import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../supabaseClient";
import {
  getPostLoginRouteByRole,
  normalizeRole,
  normalizeStaffSubRole,
} from "../../../auth/roleRoutes";

type StaffLoginPayload = {
  staffId: string;
  password: string;
};

type StaffLoginResponse = {
  success: boolean;
  role?: string;
  staffSubRole?: string;
  dashboardPath?: string;
  accessToken?: string;
  refreshToken?: string;
  mustChangePassword?: boolean;
  passwordChangePath?: string;
  message?: string;
};

const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, "") ||
  "http://localhost:4000/api";

async function loginStaff(
  payload: StaffLoginPayload,
): Promise<StaffLoginResponse> {
  const response = await fetch(`${API_BASE}/auth/staff-login`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const raw = (await response.json().catch(() => null)) as
    | {
        success?: boolean;
        message?: string;
        data?: StaffLoginResponse;
      }
    | null;

  if (!response.ok) {
    throw new Error(raw?.message || "Staff login failed.");
  }

  return raw?.data ?? { success: false, message: "Invalid server response." };
}

export default function StaffLoginPage() {
  const [form, setForm] = useState<StaffLoginPayload>({
    staffId: "",
    password: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) {
      setError(null);
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await loginStaff({
        staffId: form.staffId.trim(),
        password: form.password,
      });

      if (!result.success) {
        throw new Error(result.message || "Staff login failed.");
      }

      if (result.accessToken && result.refreshToken) {
        const { data: sessionData, error: sessionError } =
          await supabase.auth.setSession({
            access_token: result.accessToken,
            refresh_token: result.refreshToken,
          });

        if (sessionError || !sessionData.session) {
          throw new Error("Failed to establish staff session.");
        }
      }

      if (result.mustChangePassword) {
        window.location.assign(
          result.passwordChangePath || "/staff/reset-password",
        );
        return;
      }

      const normalizedRole = normalizeRole(result.role ?? "hnb_staff");
      const normalizedStaffSubRole = normalizeStaffSubRole(
        result.staffSubRole ?? null,
      );

      const fallbackPath = getPostLoginRouteByRole(
        normalizedRole,
        normalizedStaffSubRole,
      );

      window.location.assign(result.dashboardPath || fallbackPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to sign in.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #f8fafc 0%, #eef2ff 48%, #ffffff 100%)",
      }}
    >
      <div
        style={{
          maxWidth: 1120,
          margin: "0 auto",
          padding: "48px 20px 72px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.15fr) minmax(340px, 440px)",
            gap: 32,
            alignItems: "center",
          }}
        >
          <section>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 110,
                height: 34,
                padding: "0 14px",
                borderRadius: 999,
                backgroundColor: "#e0e7ff",
                color: "#3730a3",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.45,
                textTransform: "uppercase",
                marginBottom: 16,
              }}
            >
              HnB Staff
            </div>

            <h1
              style={{
                margin: 0,
                fontSize: 42,
                lineHeight: 1.08,
                fontWeight: 800,
                color: "#111827",
                maxWidth: 640,
              }}
            >
              Internal staff access for the HnB ERP workspace
            </h1>

            <p
              style={{
                margin: "16px 0 0",
                fontSize: 16,
                lineHeight: 1.8,
                color: "#475569",
                maxWidth: 700,
              }}
            >
              Sign in with your staff ID and password to access your
              operational dashboard, including internal workflows for oversight,
              verification, audit, support, finance, grievance handling, and
              governance-led platform management.
            </p>

            <div
              style={{
                marginTop: 24,
                display: "grid",
                gap: 14,
                maxWidth: 720,
              }}
            >
              {[
                "Separate from customer, provider-owner, and stylist login journeys.",
                "Built for role-specific internal dashboards and staff-only operational access.",
                "Sensitive permissions and actions should still be enforced by backend authorization after sign-in.",
              ].map((item) => (
                <div
                  key={item}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 16,
                    border: "1px solid #e5e7eb",
                    backgroundColor: "rgba(255,255,255,0.82)",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      backgroundColor: "#4f46e5",
                      marginTop: 9,
                      flexShrink: 0,
                    }}
                  />
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      lineHeight: 1.7,
                      color: "#334155",
                    }}
                  >
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section
            style={{
              width: "100%",
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 24,
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.08)",
              padding: 28,
            }}
          >
            <div style={{ marginBottom: 24 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 28,
                  lineHeight: 1.2,
                  color: "#111827",
                }}
              >
                Staff login
              </h2>

              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: "#6b7280",
                }}
              >
                Sign in with your staff ID and password to access your staff
                dashboard.
              </p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: 16 }}>
              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="staffId"
                  style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}
                >
                  Staff ID
                </label>

                <input
                  id="staffId"
                  name="staffId"
                  type="text"
                  autoComplete="username"
                  placeholder="00003"
                  value={form.staffId}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    minHeight: 46,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    fontSize: 14,
                    color: "#111827",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="password"
                  style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}
                >
                  Password
                </label>

                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    minHeight: 46,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    fontSize: 14,
                    color: "#111827",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>

              {error ? (
                <div
                  role="alert"
                  style={{
                    borderRadius: 10,
                    padding: "10px 12px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#b91c1c",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  minHeight: 48,
                  border: "none",
                  borderRadius: 10,
                  backgroundColor: isSubmitting ? "#a5b4fc" : "#4f46e5",
                  color: "#ffffff",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  boxShadow: "0 10px 24px rgba(79, 70, 229, 0.24)",
                }}
              >
                {isSubmitting ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <div
              style={{
                marginTop: 18,
                paddingTop: 18,
                borderTop: "1px solid #f3f4f6",
                display: "grid",
                gap: 10,
              }}
            >
              <Link
                to="/login"
                style={{
                  fontSize: 14,
                  color: "#4f46e5",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Go to customer login
              </Link>

              <Link
                to="/provider/login"
                style={{
                  fontSize: 14,
                  color: "#4f46e5",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Go to provider login
              </Link>

              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  lineHeight: 1.6,
                  color: "#6b7280",
                }}
              >
                This page is for HnB internal staff only. Stylists and
                customers should use their own login routes.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}